import { EventWaitlistEntryDto } from '../../../models/event/dto/event-waitlist-entry.dto';

export type WaitlistQueues = {
    /** Out-of-level players held for club approval (MYS-62). */
    approvalRequests: EventWaitlistEntryDto[];
    /** Players queued behind a full event, in the order a freed seat reaches them. */
    capacityQueue: EventWaitlistEntryDto[];
};

/**
 * Split an event's waitlist into the two queues that mean different things to
 * an admin: an approval *decision* (level) and a place in a *line* (capacity).
 *
 * Two rules here are load-bearing and easy to "tidy" into bugs:
 *
 * 1. **The capacity bucket is the complement of the level bucket**, not
 *    `reason === 'capacity'`. The server already treats an absent reason as
 *    capacity (`EventWaitlistEntry.toDto()` coalesces `reason ?? CAPACITY`, and
 *    `notifyWaitlistOnSpotOpen` matches `$or: [CAPACITY, null]` for legacy
 *    rows), so today the two spellings are identical. They stop being identical
 *    the day a third reason is added — and then a positive match would make
 *    those entries render in *neither* table. That is precisely the defect this
 *    function exists to fix, so the complement rule is chosen to fail
 *    **visible** (an entry shows up in the wrong table, which someone notices)
 *    rather than **silent**.
 *
 * 2. **Order is by `position` ascending**, which is the order
 *    `notifyWaitlistOnSpotOpen` uses when a seat frees (`orderBy position ASC`,
 *    limit 3). `GET /events/:id/waitlist` already sorts this way; sorting again
 *    here means the rendered order cannot silently stop matching who the server
 *    will actually notify if that endpoint is ever reordered.
 *
 * The input is never mutated.
 */
export const splitWaitlistQueues = (
    entries: EventWaitlistEntryDto[] | null | undefined,
): WaitlistQueues => {
    const byPosition = [...(entries ?? [])].sort(
        (a, b) => a.position - b.position || a.id - b.id,
    );

    return {
        approvalRequests: byPosition.filter((e) => e.reason === 'level'),
        capacityQueue: byPosition.filter((e) => e.reason !== 'level'),
    };
};
